// Test-only override for enableB4UPlusLaunch; undefined => read from runtime CONFIGSERVICE config.
let b4uPlusLaunchOverride: boolean | undefined;

const featureFlags = {
  enableForUPlus: false,
  // Sourced at access time from the AEM-injected runtime config. Fails CLOSED: when the
  // featureToggleConfig key is absent the launch stays OFF (pre-launch), so a missing or
  // malformed AEM config can never accidentally ship ForU+ live. The setter lets specs assign it.
  get enableB4UPlusLaunch(): boolean {
    return b4uPlusLaunchOverride ??
      ((window as any)?.['SWY']?.['CONFIGSERVICE']?.featureToggleConfig?.enableB4UPlusLaunch ?? false);
  },
  set enableB4UPlusLaunch(value: boolean) {
    b4uPlusLaunchOverride = value;
  },
  enableEmployeeDiscount: false,
  enableSnap: true,
  enablePartners: true,
  // Hides the partners carousel on the ForU+ launch landing page. false => hidden.
  enableForUPlusPartners: false,
  enablePartnersOffersV2: true,
  enableLTO: true,
  enablePreChurn: true,
  partnerPromotionBannerConfig: {
    safeway: true,
    vons: true,
    randalls: true,
    tomthumb: true,
    acmemarkets: true,
    carrsqc: true,
    jewelosco: true,
    albertsons: true,
    starmarket: true,
    shaws: true,
    pavilions: true,
    kingsfoodmarkets: true,
    haggen: true,
    andronicos: true,
    balduccis: true
  }, 
};


// Single source of truth for the B4U+/ForU+ legacy change-plan price shown in copy strings.
// These messages (B4UPLUS_MONTHLY_MSG, B4UPLUS_ANNUAL_MSG, B4UPLUS_SWITCH_SUCCESS_MSG,
// updateBanner.BODY_MONTHLY) share this price in both the forU+ and freshpass blocks; update
// here only. NOTE: pricing should ultimately come from the subscription API — replace this
// static value with API-sourced data when that work lands.
const B4UPLUS_ANNUAL_PRICE = "$39/year";

function generateUrlPaths(): any {
  const programPath = featureFlags.enableForUPlus ? 'foruplus' : 'freshpass';
  
  return {
    MANAGE_PLAN_PATH: `/${programPath}/manage`,
    SUBSCRIBE_PATH: `/${programPath}/manage/subscribe`,
    CHANGE_PLAN_PATH: `/${programPath}/manage/change`,
    CONFIRMATION_PATH: `/${programPath}/manage/confirmation`,
    CANCEL_PLAN_PATH: `/${programPath}/manage/cancel-plan`,
    FAQ_PATH: `/${programPath}/manage/${programPath}-faq`,
    GOV_ASSIST_PATH: `/${programPath}/manage/govassist`,
    UPDATE_PAYMENT_PATH: `/${programPath}/manage/update-payments`,
    PARTNERS_PATH: '/rewards/partners',
    EXPEDIA_REFERRER_PATH: '/rewards/partners/expedia',
    REWARDS_PARTNERS_DETAILS_PATH: '/rewards/partners/partner-detail',
    snapVerificationURL_UCA: "/customer-account/discount-programs/snap",
    SHOP_HOME_PATH: '/shop/home.html',
  };
}

const forUPlusData = {
  title: 'forU+',
  name: 'forU Plus',
  displayName: '{Banner} forU+',
  // ACIP-626046: SEO title / meta description / H1 for the /foruplus landing page.
  // {banner} is replaced with the banner display name (bannerConfiguration[banner].banner).
  seo: {
    title: '{banner} forU+: Unlimited Same Day Grocery Delivery Program - Grocery Rewards',
    description: "{banner} forU+ is {banner}'s unlimited same day grocery delivery program allowing you unlimited free grocery delivery for an annual fee of $39 a year. Get access to free delivery on orders of $30 or more and flash delivery at a discount. Take advantage of all the grocery delivery savings you can get with {banner} forU+ today.",
    heading: '{banner} forU+',
  },
  partnersAppNameCofig: 'Freshpass',
  code: {
    monthlySubsCode: "F9B88F7E-5F54-4536-AFC0-8B080D189CDF"
  },
  urlPath: generateUrlPaths(),
  promotionKeys: {
    discountCode: 'discountCode',
    segment: 'segment',
  },
  imageAssets: {
    subscribedPrimaryLogo: 'ocsp-web-ui/dist/assets/images/ForU+Logo.svg',
    premiumPerksLogo: 'ocsp-web-ui/dist/assets/images/JustForU_large.svg',
    couponItemLogo: 'ocsp-web-ui/dist/assets/images/ForU+Logo.svg',
    customerDetailsPrimaryLogo: 'ocsp-web-ui/dist/assets/images/ForU+Logo.svg',
  },
  snackbar: {
    discountAndActive: {
      message: "We’re sorry, this promotion is only available for new forU+ subscribers.",
      type: "error",
      show: true,
      closeOption: true,
      from: "discountCampaignAndActiveMembership",
      timer: 5000,
      closeOnClick: true,
      theme: "black",
      topVH: "12vh",
      leftVH: "80vw",
    },
  },
  errorCode: {
    DEFAULT_NO_SUBSCRIPTION_ERROR_CODE: 'OSMS-SUBSCRIPTION-00015',
    UNKNOWN_ERROR_CODE: 'OSMS-SUBSCRIPTION-0000',
    INVALID_BANNER_CODE: 'OSMS-SUBSCRIPTION-0001',
    CARD_CANNOT_BE_ACCEPTED_CODE: 'OSMS-SUBSCRIPTION-00022',
    OFFER_NO_LONGER_AVAILABLE_CODE: 'OSMS-SUBSCRIPTION-00047',
    INVALID_REASON_CODE: 'OSMS-SUBSCRIPTION-0009',
    CANCEL_DURING_BATCH_PROCESSING_CODE: "OSMS-SUBSCRIPTION-00013",
    CANCEL_WITHIN_BUFFER_TIME_CODE: "OSMS-SUBSCRIPTION-00014",
    SERVER_ERROR_CODE: "OSMS-SUBSCRIPTION-0007",
    SUBS_PAYMENT_ERROR_CODE: "OSMS-SUBSCRIPTION-00021",
    CARD_UNACCEPTED_ERROR_CODE: "OSMS-SUBSCRIPTION-00022",
    INELIGIBLE_FOR_TRIAL_ERROR_CODE: "OSMS-SUBSCRIPTION-00069",
    UNABLE_TO_PROCESS_ERROR_CODE: "OSMS-SUBSCRIPTION-00070",
  },
  WEBSOURCE: {
    EXPEDIA: 'WEB-EXPEDIA',
  },
  errorMessages: {
    UNKNOWN_ERROR_MESSAGE: 'Unknown error. Please contact the support team for resolution.',
    INVALID_BANNER_MESSAGE: `forU+ isn't available for {banner} yet, but it's coming soon. Stay tuned!`,
    CARD_CANNOT_BE_ACCEPTED_MESSAGE: 'Card cannot be accepted. Please confirm card details entered correctly or continue with another form of payment.',
    OFFER_NO_LONGER_AVAILABLE_MESSAGE: 'We\'re sorry, this offer is no longer available.',
    DEFAULT_ERROR_MESSAGE: 'Sorry, we\'re having technical difficulties and working on fixing them, please check back later.',
    INVALID_REASON_MESSAGE: "Cancellation reason is invalid.",
    CANCEL_DURING_BATCH_PROCESSING_MESSAGE: "Subscription is renewing and cannot be cancelled.",
    CANCEL_WITHIN_BUFFER_TIME_MESSAGE: "Your plan is currently being renewed and cannot be cancelled at this time.",
    SERVER_ERROR_MESSAGE: "Internal server error occured.",
    UNKNNOWN_ERROR_MESSAGE: "Unknown error. Please contact the support team for resolution.",
    SUBS_PAYMENT_ERROR_MESSAGE: "Your enrollment cannot be processed. Please update your payment method and try again.",
    CARD_UNACCEPTED_ERROR_MESSAGE: "Card cannot be accepted. Please confirm card details were entered correctly or continue with another form of payment",
  },
  freshpassOffers: {
    ltoTitle: "Limited time forU+ savings",
    freshpassLTO: "FRESHPASS LTO",
    freshpassPerks: "FRESHPASS",
  },
  partnersOffers: {
    PARTNER_OFFER_TITLE_V2: "New! Exclusive partner savings",
    PARTNER_OFFER_SUBTITLE_V2: "Enjoy these benefits with your subscription.",
    PARTNER_OFFER_TITLE: "Partner offers",
    PARTNER_OFFER_SUBTITLE: "Subscribe to forU+ and get access to these exclusive partnership benefits.",
    VALID_PARTNER_PROGRAM_TYPES: ["freshpass"],
    PARTNER_BANNER_FILTER: "partnerbanner",
  },
  notificationMsg: {
    activeEnrollmentSuccessMsg: 'forU+ is active! Perks are on their way.',
    trialEnrollmentSuccessMsg: 'forU+ free trial is active! Perks are on their way.',
    canceledMsg: 'Your current forU+ plan has been cancelled effective immediately per your request.',
    cancelProcessedMsg: '{planType}: Your cancellation request has been processed',
    cancelTrialProcessedMsg: '{isTrial} - {planType}: Your cancellation request has been processed.',
    updatePlanSuccessMsg: 'Your plan has been updated successfully.',
  },
  planCard: {
    selectAction: "Select",
    SNAPAction: "See if I'm eligible",
    plusIcon: "ocsp-web-ui/dist/assets/images/plus.svg",
    moreIcon: "ocsp-web-ui/dist/assets/images/more.svg",
    greenIcon: "ocsp-web-ui/dist/assets/images/green.svg",
    checkmarkCircle: "ocsp-web-ui/dist/assets/images/checkmark-circle.svg",
    trialLabel: "-day free trial",
    creditIcon: "ocsp-web-ui/dist/assets/images/gift_Icon.svg",
    annualPlanDescription: "Which is just {monthlyPrice} + any applicable taxes. Auto-renews at {price} unless canceled by renewal date.",
    annualDiscountPlanDescription: "+Tax, Auto-renews at {renewPrice} unless canceled by renewal date.",
    monthlyPlanDescription: "Auto-renews at {price} + any applicable taxes unless canceled by renewal date.",
    SNAPPlanDescription: "Requires yearly re-verification or is automatically canceled.",
    AnnualPlan: "Annual Plan",
    MonthlyPlan: "Monthly Plan",
    SNAP: "SNAP",
    annualCreditLabel: "Monthly credit - a $60 value*",
    snapNotVerified: "Not Verified",
    planDescriptionForSNAPVerified: "Auto-renews at {pricingDetailLine1} + any applicable taxes. Requires yearly re-verification or is automatically canceled.</b>",
    monthlyPlanDescriptionForSNAPVerified: "+Tax, Auto-renews at {price}",
    annualOrmonthlySheerLabel: "Your eligibility for government assistance has been verified! Your plan will auto-renew at {price}",
    successColor: "success",
    snapBannerDescription: "Select government assistance programs get 50% off.",
    freshpassBenefitDescription: "Select government assistance programs get all the forU+ benefits at just",
    campaignTitleText: "forU+ benefits for half the price",
    planPerYear: "/year or ",
    planPerMonth: "/month.",
    monthlyDescriptionForSNAPVerified: "Auto-renews at {price} + any applicable taxes. Requires annual re-verification or is automatically canceled by renewal date.",
    monthlyDescriptionForSNAPVerifiedRef: "Auto-renews at {price} + any applicable taxes. Requires annual re-verification or is automatically canceled by renewal date.",
    AnnualDescriptionForSNAPVerified: "+ Any applicable taxes. Requires yearly re-verification or is automatically canceled.",
  },
  freshpassData: {
    freshpassTitle: "{banner} forU+",
    // Manage-page header tagline, under freshpassTitle (Figma 7331:21289).
    freshpassTagline: "Unlimited free same-day delivery and more",
    freeTrialTitle: "Free Trial",
    freshpassManageTitle: 'Manage your {banner} forU+ plan',
    annualPlanText: "Annual Plan",
    manageText: "manage",
    snapKey: "snap",
    forTheFirstYearText: "for the first year",
    snapVerifiedActivationMessage: "Your eligibility for government assistance has been verified! Your plan will auto-renew at ${fee}{pricingDetail} (+tax)",
    pendingActivationStatusMessage: "Your plan will auto renew at ${pendingFee}{pendingPricingDetail} after your current plan ends.",
    suspendedGovElibilityText: "Your forU+ plan is currently suspended. Please re-verify your ",
    govAssistanceElibilityText: "government assistance eligibility.",
    pleaseReverifyEligibilityText: "Please re-verify your eligibility.",
    snapTrialDescription: "+ Any applicable taxes. Charged after {trial-duration}-day free trial. Annual re-verification will be required for continued access to discounted subscription price.",
    snapNonTrialDescription: "+ Any applicable taxes. Annual re-verification will be required for continued access to discounted subscription price.",
    fpBenefitsDescription: "Savings made simple, with free delivery, exclusive partner offers, plus all the benefits of forU.",
    snapBottomText: "Select government programs only include SNAP.",
    snapBottomText2: "More programs coming soon!",
    lifeTimeSavingsText: "Lifetime forU+ Savings",
    hoursSavedText: "Hours Saved",
    continueSubscription: "Continue forU+ Subscription",
    noThanksContinueShoping: "No Thanks. Continue Shopping",
    suspendedSecondaryUserPrompt: "Your {banner} forU+ plan is currently disabled due to an issue with your payment method. Please contact the primary forU+ account holder to update the payment method.",
    suspendedSecondaryUserAndGovAssistancePrompt: 'Your {banner} forU+ plan is currently disabled. Please contact the primary forU+ account holder to re-verify their government assistance eligibility.',
    lifeTimeSavingsTooltipDisclaimer: "Total savings are calculated starting on 6/22/2022. If you subscribed to forU+ after this date, your total savings are calculated starting on your subscription date. Total savings calculation is based on discounts to grocery delivery fees and redemption of perks listed on the forU+ subscription page."
  },
  subscribeData: {
    step2HeaderTitle: '{Banner} forU+ sign up',
    redirectedTo: "You will be redirected to",
    shop: "Shop",
    likeToProceed: "you like to proceed?",
    completed: "Completed",
    step: "Step",
    step1: "Step 1",
    step2: "Step 2",
    step3: "Step 3",
    currentStep: "Current step",
    agreeTo: "I agree to the",
    cancel: "Cancel",
    freshPass: "forU+",
    zipHeading: "Unlimited free same-day delivery for",
    zipAltText: "ForU Plus logo",
    zipCtaFreeTrial: "Try it FREE for {trial-duration} days",
    zipCtaRegular: "Get started",
    zipExtendedTrial: "plus a {trial-duration}-day free trial",
    noDeliveryZipDescription: "Apologies, forU+ is not available in your area.",
    selectPlanZipSolicitationLabel: "Enter ZIP Code to get started",
    selectPlanFreeTrialMessage: "Get started by selecting a plan below:",
    selectPlanRegularMessage: "Save extra money on your monthly deliveries!",
    annualPlanDefaultRibbon: "Best Value",
    annualPlanDescription: "Which is equal to just {price} + any applicable taxes.<br /><b>Auto-renews at {renewPrice} unless canceled by renewal date.</b>",
    snapMonthlyPlanDescription: "+Tax, Billed monthly!<br />Requires yearly verification or is automatically cancelled. Discount plan available for up to 24 months",
    snapMonthlyPlanDescriptionSpacing: "+Tax, Billed monthly!<br />Requires yearly verification or is automatically cancelled.<br />Discount plan available for up to 24 months",
    annualPlanCtaFreeTrial: "Start {trial-duration}-day Free Trial",
    annualPlanCtaRegular: "Select",
    annualPlanIsDefaultPlan: "true",
    monthlyPlanDefaultRibbon: "Save 35%",
    monthlyPlanDescription: "+Tax, Billed monthly.<br /> Auto-renews{price}.",
    monthlyPlanDescription2: "+ Any applicable taxes. <b>Auto-renews at {price} unless canceled by renewal date.</b>",
    discountRibbonLabel: "NA",
    paymentNote: "Note: Add or select a credit or debit card for your subscription. Your payment card will not be charged until your free {trial-duration}-day trial has ended.",
    paymentNoteNoTrial: "Note: Your payment card will be charged immediately when confirming sign up for ForU+.",
    trialTaxMessage: "Charged after {trial-duration}-day free trial. Auto-renews at {annual-plan-price}{conjunction}{monthly-plan-price}.",
    taxMessage: "Auto-renews at {annual-plan-price}{conjunction}{monthly-plan-price}.",
    bannerPaymentTermsMsg: "{Banner}"
  },
  subscriptionAuthoringFields: {
    paymentFreeTrialButtonCTA: "Continue",
    paymentButtonCTA: "Continue",
    thankYouPaymentDetailsLabel: "Payment Details",
    thankYouSubscriptionLabel: "Current Subscription Plan ",
    thankYouMsg: "You're all set! Thank you for registering, your forU+ membership is now active. ",
    changePlanLabel: "Manage Plan",
    changePlanLink: "/content/www/safeway/en/delivery-subscription/manage-delivery-subscription.html",
    updatePaymentLabel: "Update Payment Method",
    updatePaymentLink: "/content/www/safeway/en/account/payment.html",
    showNowButtonLabel: "Shop Now",
    showNowButtonLink: "/",
    selectPlanChangeZipcode: "Change ZIP Code",
    selectPlanTitle: "Choose your plan",
    selectPlanHeading: "Unlimited Delivery is available in",
    freshpassHeading: "Choose a plan",
    freshpassHeading2: "Select a government assisted plan",
    selectPlanTaxes: "(+ tax)",
    selectPlanTaxesSnap: "(+ any applicable taxes)",
    accountTitle: "Set up your account",
    freshPassNoDelivery: "We're sorry, delivery with forU+ is not available in ",
    freshPassBenefitsTitle: "forU+ Benefits",
    paymentTitle: "Payment details",
    restrictedZipDescription: "Apologies, forU+ is not available in your area",
    sisterBannerDescription: "forU+ is available at our sister store",
    // ForU+ launch: terms-link brand for the payment authorization checkbox (no space, no ™).
    paymentTermsBrand: "{Banner} forU+",
    paymentAuthorizeMsgAferPlanOne: ". I consent to and authorize storage of my debit and/or credit card data and authorize charges to such card for purchases, subscription fees after the",
    paymentAuthorizeMsgAferPlanTwo: "-day free trial and for subsequent auto-renewals until I cancel.",
    terms: "terms",
    paymentAuthorizeMsgCurrentPlan:
      ". I consent to and authorize storage of my debit and/or credit card data and authorize charges to such card for current subscription fees and future auto-renewals until I cancel.",
    subscriptionPlan: "subscription plan",
  },
  manage: {
    CURRENT_PLAN_ICON: "ocsp-web-ui/dist/assets/images/ForU+Logo.svg",
    PAYMENT_METHOD: "Payment Method",
    CURRENT_PAYMENT_METHOD: "Payment Method",
    ADD_A_CREDIT_OR_DEBIT_CARD_FOR_YOUR_SUBSCRIPTION: "Add a credit or debit card for your subscription",
    UPDATE_PAYMENT_METHOD: "Update Payment Method",
    ADD_NEW_CARD: "Add New Card",
    EXPIRED: "Expired",
    CONFIRM_CVV: "Confirm CVV",
    CHANGE_PLAN: "Change Plan",
    SWITCH_TO_FORUPLUS: "Switch to forU+",
    B4UPLUS_MONTHLY_MSG: "FreshPass is changing to {banner} forU+. To keep enjoying delivery and pickup savings, switch to the annual plan at " + B4UPLUS_ANNUAL_PRICE + " before your renewal on {renewalDate}.",
    B4UPLUS_ANNUAL_MSG: "FreshPass is moving to {banner} forU+, now " + B4UPLUS_ANNUAL_PRICE + ". You’ll auto-renew on {renewalDate}—keep enjoying your current benefits until then.",
    B4UPLUS_LEARN_MORE_URL: "/4uplus/change-management.html",
    B4UPLUS_SWITCH_SUCCESS_MSG: "Great! You’ll continue to receive FreshPass benefits. Your {banner} forU+ plan will auto renew at " + B4UPLUS_ANNUAL_PRICE + " after your current plan ends.",
    // The leading "$" is a literal dollar sign in the displayed copy (double quotes, not a JS template literal); {currentPrice} etc. are runtime-replaced tokens.
    B4UPLUS_CURRENT_UNTIL_EXPIRY: "${currentPrice}{pricingDetail} (+tax) until {currentExpiryDate}",
    B4UPLUS_ANNUAL_STARTS_MSG: "Your annual plan will start on {renewalDate}",
    SNAP_AUTO_CANCEL_MSG: "Your plan will be automatically canceled on {cancelDate}",
    Cancel_FreshPass: "Cancel forU+",
    YOUR_PLAN_CAN_NOT_BE_CHANGED_AGAIN_UNTIL_THE_CURRENT_UPDATE_IS_COMPLETE: "Your plan can not be changed again until the current update is complete.",
    MONTHLY: "Monthly",
    ANNUAL: "Annual",
    ANNUAL_PLAN: "Annual Plan",
    B4UPLUS_ANNUAL_PLAN: "Annual plan",
    MONTHLY_PLAN: "Monthly Plan",
    ANNUAL_SUBSCRIPTION: "Annual subscription",
    MONTHLY_SUBSCRIPTION: "Monthly subscription",
    // Header label for a secondary user on trial. Distinct from the shared FREE_TRIAL below,
    // which four other components render as a plain status and must keep reading "Free Trial".
    FREE_TRIAL_SUBSCRIPTION: "Free Trial",
    DISCOUNTED_PLAN_BEGINS_MSG: "Discounted {planType} plan begins</br>{renewalDate}",
    CURRENT_PLAN_BEGINS_MSG: "{planType} plan begins {renewalDate}",
    CURRENT_PLAN_RENEWS_MSG: "Renews {renewalDate}",
    TRIAL: "TRIAL",
    ACTIVE: "ACTIVE",
    SUSPENDED: "Suspended",
    PENDING_ACTIVATION: "PENDING_ACTIVATION",
    PROCESSING: "PROCESSING",
    PENDING_CANCELLATION: "PENDING_CANCELLATION",
    CHANGE_PLAN_CON: "CHANGE_PLAN",
    FREE_TRIAL_ENDS_ON_MSG: "Free trial ends on {currentPlanExpiryDate}",
    FREE_TRIAL: "Free Trial",
    MONTHLY_SUBSCRIBER: "Monthly Subscriber",
    ANNUAL_SUBSCRIBER: "Annual Subscriber",
    CANCELLED: "CANCELLED",
    PLAN_PER_YEAR: "/year",
    PLAN_PER_MONTH: "/month",
    TAX: "(+tax)",
    CURRENT_PLAN_PRICE_DETAILS: "${currentPrice}{pricingDetail} (+tax)",
    AUTO_RENEWS_AT: "Auto-renews at $",
    AUTO_RENEWS_AT_REGULAR_PLAN_PRICE: "Auto-renews at ${regularPlanPrice}{regularPricingDetails}.",
    AUTO_RENEWS_AT_REGULAR_PLAN_PRICE_TAX: "Auto-renews at ${regularPlanPrice}{regularPricingDetails} (+tax).",
    AUTO_RENEWS_AT_CURRENT_FEE_TAX: 'Auto-renews at ${currentFee}{currentPricingDetails} (+tax).',
    AUTO_RENEWS_AT_PENDING_FEE: 'Auto-renews at ${pendingFee}{pendingPricingDetails}.',
    AUTO_RENEWS_AT_PENDING_FEE_FOR_FIRST_YEAR: 'Auto-renews at ${pendingFee}{pendingPricingDetails}.',
    AUTO_RENEWS_AT_PENDING_FEE_TAX: 'Auto-renews at ${pendingFee}{pendingPricingDetails} (+tax).',
    CURRENT_PLAN_UNTIL_EXPIRY_DATE: '${currentPrice}{pricingDetail} until {currentExpiryDate} (+tax)', 
    UNTIL: "until",
    CURRENT_PLAN_ENDS_MSG: "Ends {expiryDate}",
    DISCOUNT_PLAN_BEGINS_MSG: "{discounted} {planType} begins </br> {renewalDate}",
    PLAN_BEGINS_MSG: "{planType} plan begins {renewalDate}",
    VIP_CUSTOMER_SERVICE_PHONE_LINE: "VIP Customer Service Phone Line",
    FRESH_PASS_SUBSCRIBERS_RECEIVE_QUICK_CUSTOMER_SERVICE_FROM_A_DEDICATED_SUPPORT_TEAM: "{banner} forU+ subscribers receive quick customer service from a dedicated support team.",
    Num_855_767_2545: "855-767-2545",
    SHOP_NOW: "Shop Now",
    DELIVERY_SUBSCRIPTION: "DELIVERY_SUBSCRIPTION",
    FRESHPASS_MANAGE_SUBSCRIPTION: "forU+ - Manage Subscription",
    NOTIFY_MESSAGE: "notifyMessage",
    F9_B88_F7_E_5_F54_4536_AFC0_8_B080_D189_CDF: "F9B88F7E-5F54-4536-AFC0-8B080D189CDF",
    YOUR_FRESH_PASS_PLAN_IS: "Your {banner} forU+ plan is currently disabled due to an issue with your payment method. Please update your payment method.",
    // Suspended secondary user (Figma 1768:41713) — no link; they cannot update the payment
    // method themselves. YOUR_FRESH_PASS_PLAN_IS above is the primary-user variant (1768:40756).
    SUSPENDED_SECONDARY_MESSAGE: "Your {banner} forU+ plan is currently disabled due to an issue with your payment method. Please contact the primary forU+ account holder to update the payment method.",
    YOUR_CURRENT_FRESH_PASS_PLAN: "Your current {banner} forU+ plan has been <b>cancelled</b> per your request, you can still continue to use {banner} forU+ until",
    YOUR_CURRENT_FRESH_PASS_PLAN_HAS_BEEN: "Your current {banner} forU+ plan has been <b>cancelled</b> per your request. Your Free Trial ends on",
    // Free trial + pending cancellation (Figma 7331:19794). Replaces the old two-fragment
    // concatenation for B4U+: one sentence, "canceled" with one L, and a single date — the
    // second half says "until then" rather than repeating it. The two fragments above are
    // still used by the legacy FreshPass manage flow, so they are left alone.
    PENDING_CANCELLATION_TRIAL_MESSAGE: "Your current {banner} forU+ plan has been <b>canceled</b> per your request. Your Free trial ends on {expirationDate}. You can still continue to use your benefits until then.",
    // One-time welcome shown on the first manage-page visit after a successful B4U+ enrollment
    // (Figma 7331:23265). Deliberately shorter than confirmation's
    // WELCOME_TO_FRESH_PASS_THANKS_FOR_SIGNING_UP_YOUR_PERKS_ARE_ON_THE_WAY_IF, which that page
    // keeps using.
    NEW_SUBSCRIBER_WELCOME_MESSAGE: "Welcome to {banner} forU+! Thanks for signing up.",
    // Non-trial pending cancellation (Figma 7331:19651). Same shape as the trial variant above,
    // but this one does repeat the banner rather than saying "your benefits".
    // YOUR_CURRENT_FRESH_PASS_PLAN above is the old B4U+ copy, kept for the legacy flow.
    PENDING_CANCELLATION_MESSAGE: "Your current {banner} forU+ plan has been <b>canceled</b> per your request. You can still continue to use {banner} forU+ until {expirationDate}.",
    // Immediate (refund-eligible) cancellation, shown on the brief display-only stop on the
    // manage page before the redirect to /foruplus. Separate from notificationMsg.canceledMsg,
    // which CancelFreshpassSummaryComponent also reads through its flag-forked programDisplayData
    // and passes to the AEM toast unresolved — a {banner} token added there would leak raw.
    CANCELLED_IMMEDIATELY_MESSAGE: "Your current {banner} forU+ plan has been canceled effective immediately per your request.",
    FRESH_PASS_PLAN_WILL_BECOME_EFFECTIVE_AFTER_THE_CURRENT_PLAN_EXPIRES_ON: "forU+ plan will become effective after the current plan expires on ",
    YOUR_FREE_TRIAL_ENDS_ON: "Your free trial ends on ",
    YOU_LL_BE_AUTOMATICALLY_ENROLLED_INTO_THE: "you’ll automatically be enrolled into the ",
    TRIAL_STATUS_MESSAGE: "Free trial automatically converts to ${fee}{pricingDetail} membership + any applicable taxes unless canceled by {expirationDate}.",
    FRESH_PASS_PLAN_ON: "{banner} forU+ plan on ",
    YOU_CAN_STILL_CONTINUE_TO_USE_FRESH_PASS_UNTIL: "You can still continue to use {banner} forU+ until ",
    YOUR: "Your $",
    MANAGE_MY_PLAN: "Manage My Plan",
    FRESHPASS: "forU+",
    NEED_HELP: "Need help?",
    HERE: "here",
    RESTRICTIONS_APPLY_FULL_PROGRAM_TERMS_CONDITIONS: "*Restrictions apply. Refer to {banner} forU+ Terms & Conditions below.",
    RESTRICTIONS_APPLY_FULL_PROGRAM_TERMS_CONDITIONS_LINK: "*Restrictions apply. Refer to {banner} forU+ Terms & Conditions",
  },
  confirmation: {
    SAVING_SUMMARY_MODAL: "saving-summary-modal",
    ESTIMATE_ANNUAL_SAVINGS_TERMS: "estimate annual savings terms",
    TERMS_INFO_MODAL_TEXT: "termsInfoModalText",
    SAVINGS_SUMMARY: "Savings Summary",
    updateBanner: {
      TITLE: "FreshPass is updating",
      BODY_MONTHLY: "FreshPass is changing to {banner} forU+. To keep enjoying delivery and pickup savings, switch to the annual plan at " + B4UPLUS_ANNUAL_PRICE + " before your renewal date.",
      BODY_ANNUAL: "Your FreshPass plan is changing to the new {banner} forU+ plan with a lower annual fee. You can continue enjoying your FreshPass benefits until then.",
      SWITCH_PLAN: "Switch plan",
      SWITCH_PLAN_URL: "/freshpass/manage/change.html",
      LEARN_MORE: "Learn more",
      LEARN_MORE_URL: "/4uplus/change-management.html",
      CLOSE_ARIA: "close",
      ICON_ALT: "information",
    },
    FRESH_PASS_ANNUAL_SUBSCRIBERS_COULD_SAVE_UP_TO_425_PER_YEAR: "forU+ annual subscribers could save up to $425 per year",
    ESTIMATED_ANNUAL_SAVINGS: "Estimated Annual Savings",
    SAVED_AND_COUNTING: " saved, and counting!",
    CONGRATS_YOU_VE_SAVED: "Congrats! You've saved ",
    WITH_FRESH_PASS: " with forU+",
    WE_HOPE_YOU_RE_ENJOYING_THE_PERKS_AND_THE_SAVINGS: "We hope you're enjoying the perks and the savings!",
    OK_GOT_IT: "Ok, Got It",
    TRIAL: "trial",
    PROCESSING: "processing",
    ANNUAL_PLAN: "Annual Plan",
    ANNUAL: "annual",
    MONTHLY: "monthly",
    YOUR_FRESHPASS_PERKS: "Your {banner} forU+ Perks",
    FRESHPASS_PERKS: "forU+ Perks",
    YOUR_FRESH_PASS_BENEFITS: "{banner} forU+ Benefits",
    ACCOUNT_SUSPENDED: "Account Suspended",
    YOUR_FRESH_PASS_PLAN_IS_CURRENTLY_DISABLED_DUE_TO_AN_ISSUE_WITH_YOUR: "Your {banner} forU+ plan is currently disabled due to an issue with your payment method. Please update your",
    PAYMENT_METHOD: "payment method.",
    WELCOME_TO_FRESH_PASS_THANKS_FOR_SIGNING_UP_YOUR_PERKS_ARE_ON_THE_WAY_IF: "Welcome to {banner} forU+! Thanks for signing up, your perks are on the way. If you've redeemed them all, check back soon.",
    DON_T_MISS_OUT_ON_FREE_SAVINGS: "Don't miss out on free savings!",
    FRESH_PASS_ANNUAL_PLAN_SUBSCRIBERS_GET_A_5_CREDIT_EVERY_MONTH: "forU+ annual plan subscribers get a $5 credit every month",
    AVAILABLE_TO_YOU: "Available to you",
    UPGRADE_NOW: "Upgrade Now",
    ADDED: "Added",
    COUPON_CLIP_SESSION_IS_EXPIRED_RELOADING_THE_PAGE: "Coupon clip session is expired. Reloading the page.",
    IN_STORE: "In-store",
    invalidReasonCode: "Cancellation reason is invalid.",
    cancelDuringBatchProcess: "Subscription is renewing and cannot be cancelled.",
    cancelWithinBufferTime: "Your plan is currently being renewed and cannot be cancelled at this time.",
    serverError: "Internal server error occured.",
    unknownError: "Unknown error. Please contact the support team for resolution.",
    subsPaymentError: "Your enrollment cannot be processed. Please update your payment method and try again.",
    subsDefaultError: "Sorry, we're having technical difficulties and working on fixing them, please check back later.",
    cardUnacceptedError: "Card cannot be accepted. Please confirm card details were entered correctly or continue with another form of payment",
    PARTNERS: "Partners",
    PRTNRS: "prtnrs",
    NO_PRTNRS: "no-prtnrs",
    CONFIRMATION: "confirmation",
    WELCOME: "Welcome",
    ALBERTSONS: "albertsons",
    STARMARKET: "starmarket",
    PAVILIONS: "pavilions",
    ERROR_FETCHING_LOGO_LIST: "error fetching logo list",
    SHAWS: "shaws",
    MASK: "mask",
    FRESHPASSTILEPARSYS: "freshpasstileparsys",
    ERROR_FETCHING_EXPERIENCE_FRAGMENT: "error fetching experience fragment",
    CLASS: "milestone-modal",
    backdrop: "static",
    ariaLabelledBy: "modal milestone dropdown",
    ariaDescribedby: "milestone dropdown",
    timeToShow: "timeToShow",
    ERROR_FETCHING_PARTNERS_DATA: "error fetching partners data",
    notifyMessage: "notifyMessage",
    OFFER_DETAILS: "Offer Details",
    defaultSuspendedErrorMessage: 'Your forU+ plan is currently disabled due to an issue with your payment method. Please update your',
    defaultSuspendedErrorLinkText: 'payment method'
  },
  notificationModal: {
    promotionExpired: "This promotion has expired",
    viewOffers: "View current offers",
    weAreSorry: "we're sorry, this",
    noLongerAvailable: "is no longer available",
    inEligibleForTrialErrorProps: {
      errorCode: "OSMS-SUBSCRIPTION-00069",
      title: 'Not eligible for free trial',
      message: 'Looks like you’ve already experienced the perks of the free trial. If you continue, you will be charged for the subscription.',
      primaryBtnText: 'Continue with forU+ subscription',
      enableBackButton: true,
      backButtonText: 'Go back',
    },
    unableToProcessErrorProps: {
      errorCode: "OSMS-SUBSCRIPTION-00070",
      title: 'We’re unable to process your order.',
      message: 'You have not been charged and we apologize for any inconvenience. If you would like future assistance, please call Customer Support at 855-799-4584.',
      primaryBtnText: 'Close',
      enableBackButton: false,
      backButtonText: '',
    },
  },
  personalizedSavings: {
    savedSoFar: "saved so far with forU+",
  },
  savingsSummary: {
    existingDeliveryEngagedText:
      "Based on average delivery orders for existing delivery engaged forU+ subscribers from 4/1/2021 to 9/23/2021 utilizing non-prime order time slots (delivery fees vary – value based on existing slot utilization of subscribers). Savings calculation also includes $20 savings for first time members on first $75 online purchase, monthly perk values, and product category savings that annual subscribers enjoy. Product savings value is calculated based on existing subscriber data",
    close: "CLOSE",
  },
  termsAndConditions: {
    forMoreInfo: "For more information, see our",
    faq: "FAQ",
    termsNconditions: "Terms and Conditions",
  },
  cancelFreshPass: {
    cancelPlan: "Cancel Plan",
    reasonForCancellationSelect: "Reason for Cancellation (select one):",
    reasonForCancellation: "Reason for Cancellation",
    additionalFeedback: "Additional Feedback",
    noOfCharacters: "No. of characters 0 / 400",
    cancelUserText: "We hate to see you go. Are you sure you want to end your subscription?",
    yesCancelPlan: "Yes, Cancel Plan",
    keepPlan: "No, Keep Plan",
    discountPlanTitle: "Interested in a <br/> one-time discount?",
    discountPlanSecondTitle: "We appreciate you—and want to give you a little gift to show it.",
    discountPlanBtnDesc: "Yes, I'm interested",
    discountPlanCancelBtnDesc: "Continue to cancel",
    benefitsBtnDesc: "Get discounted price",
    benefitsSplOffer: "Special Offer",
    benefitsRetentionPlanAnnual: "Enjoy one year of {banner} forU+ annual for",
    benefitsRetentionPlanAnnualForSrOnly: "Enjoy one year of {banner} forU+ annual for new price",
    benefitsTaxInfo: "Which is just {pricingDetailLine2} + any applicable taxes.",
    benefitsRenewMessage:"Which is just {pricingDetailLine2} + any applicable taxes.<br/> Auto-renews at ${price} unless canceled by renewal date.",
    infoMsgForFPCancelRefundNotEligible: "If you cancel today, you keep your {banner} forU+ benefits until ",
    // Ends with a period and takes no date — unlike infoMsgForFPCancelRefundNotEligible, which
    // ends with "until " and has the plan expiry appended (Figma 7331:19297 vs 7331:19201).
    infoMsgForFPCancelRefundEligible: "Because you have not used the {banner} forU+ service and are canceling within 15 days, your subscription fee will be refunded.",
    beforeYouGoMessage: 'Before you go, please tell us why you’re not keeping your {banner} forU+ subscription.'
  },
  cancelFreshPassSuccess: {
    vipCustomerService: "VIP Customer Service Phone Line",
    planType: "forU+ members receive quick customer service from a dedicated support team.",
    SUSPENDED: "SUSPENDED",
  },
  changePlanSummary: {
    offYourSubscription: "50% off your subscription",
  },
  annualPlan: {
    taxText: "+Tax, Which is equal to $",
    monthText: "/month!",
    renewelText: "Auto-renews at",
    selectAnnual: "Select Annual",
    currentPlan: "Current Plan",
  },
  changePlan: {
    govtAssisted: " government assisted",
    month: "month",
    year: "year",
    trial: "Trial",
    changePlanText: "Change plan",
    planTextOne: " If you select the",
    plan: "plan, ",
    planTextTwo: "it will become effective after the current plan expires on",
  },
  monthlyPlan: {
    taxMonthly: "+Tax, Billed monthly!",
    selectMonthly: "Select Monthly",
    months: "months",
    autoRenew: "Auto-renews.",
  },
  cancelFreshpassSummary: {
    monthlyFreshpass: "Monthly forU+",
    annualFreshPass: "Annual forU+",
  },
  paymentModel: {
    API_FAIL_ERROR_MESSAGE: "Sorry, we're having technical difficulties and working on fixing them, please check back later.",
    UNKNOWN_ERROR_MESSAGE: "Unknown error. Please contact the support team for resolution.",
    update: "Update",
    FORUPLUS_PROMO_TITLE: "forU+, a simple way to save",
    FORUPLUS_PROMO_BODY: "Unlimited free delivery, free Flash™ pickup, and $2 Flash delivery.",
  },
  prechurn: {
    retentionDiscountType: 'retentionannual',
    retentionCampaignType: 'retention',
    successStatusMessage: 'Great! You’ll continue to receive forU+ benefits. Your plan will auto renew at ${pendingFee}{pendingPricingDetail} after your current plan ends.',
    discountPlanBeginsMessage: "Discounted {pendingSubscriptionType} plan begins</br>{renewalDate}"
  },
  partnersBanners: {
    bannerHeading: "Select your preferred store to claim your discount",
    bannerTitle: "Select your store, create an account or sign in. Then subscribe to forU+ to claim your discount.",
    joinFreshpass: "Join forU+",
    FAQ: "Frequently Asked Questions",
    termsAndCondition: "Terms & Conditions",
    continue: "Continue",
  },
  partnersLanding: {
    ERROR_DISCOUNT_TITLE: "Sorry, this discount is no longer available.",
    ERROR_DISCOUNT_MESSAGE: "Oops! This offer is either invalid or has expired. Sign up now and start receiving forU+ benefits today.",
    ERROR_MISSING_BANNERS_TITLE: "Sorry, this page is currently down.",
    ERROR_MISSING_BANNERS_MESSAGE: "Oops! Please check back soon or sign up now and start receiving forU+ benefits today.",
    DISCOUNT_ERROR: "DISCOUNT_ERROR",
    BANNER_ERROR: "BANNER_ERROR",
  },
  updatePayments: {
    API_FAIL_ERROR_MESSAGE: "Sorry, we're having technical difficulties and working on fixing them, please check back later.",
    paymentUpdatedMsg: "You're  all set! Your payment method has been updated.",
  },
  snapHowItWorks: {
    benefitsTitle: "3 Easy Steps to Sign Up!",
    howItWorksHeading: "Verify your eligibility",
    howItWorksText: "Complete the verification process in your account settings.",
    paymentHeading: "Enter a payment method",
    freeTrailEndText: "When your free trial ends, this payment method will be used for any recurring monthly charges.",
    enjoyBenefits: "Enjoy the benefits!",
    freeDeliveryText: "Get free delivery and all the other exclusive perks.",
  },
  snapBenefits: {
    benefitsTitle: "Your forU+ benefits",
    termsAndConditions: "Restrictions apply. Refer to forU+ Terms & Conditions below.",
    dontQualifyText: "Don't qualify? View our other plans.",
    childList: "childList",
  },
}

const freshPassData = {
  title: 'Freshpass',
  name: 'Freshpass',
  displayName: 'Freshpass',
  partnersAppNameCofig: 'Freshpass',
  code: {
    monthlySubsCode: "F9B88F7E-5F54-4536-AFC0-8B080D189CDF"
  },
  urlPath: generateUrlPaths(),
  promotionKeys: {
    discountCode: 'discountCode',
    segment: 'segment',
  },
  imageAssets: {
    subscribedPrimaryLogo: 'https://images.albertsons-media.com/is/image/ABS/freshpass-logo?$lplogo$&defaultImage=Not_Available',
    premiumPerksLogo: 'ocsp-web-ui/dist/assets/images/freshpass_discounts_img.svg',
    couponItemLogo: 'ocsp-web-ui/dist/assets/images/freshpass-logo.svg',
    customerDetailsPrimaryLogo: 'ocsp-web-ui/dist/assets/images/freshpass-logo-v2.svg',
  },
  snackbar: {
    discountAndActive: {
      message: "We’re sorry, this promotion is only available for new forU+ subscribers.",
      type: "error",
      show: true,
      closeOption: true,
      from: "discountCampaignAndActiveMembership",
      timer: 5000,
      closeOnClick: true,
      theme: "black",
      topVH: "12vh",
      leftVH: "80vw",
    },
  },
  errorCode: {
    DEFAULT_NO_SUBSCRIPTION_ERROR_CODE: 'OSMS-SUBSCRIPTION-00015',
    UNKNOWN_ERROR_CODE: 'OSMS-SUBSCRIPTION-0000',
    INVALID_BANNER_CODE: 'OSMS-SUBSCRIPTION-0001',
    CARD_CANNOT_BE_ACCEPTED_CODE: 'OSMS-SUBSCRIPTION-00022',
    OFFER_NO_LONGER_AVAILABLE_CODE: 'OSMS-SUBSCRIPTION-00047',
    INVALID_REASON_CODE: 'OSMS-SUBSCRIPTION-0009',
    CANCEL_DURING_BATCH_PROCESSING_CODE: "OSMS-SUBSCRIPTION-00013",
    CANCEL_WITHIN_BUFFER_TIME_CODE: "OSMS-SUBSCRIPTION-00014",
    SERVER_ERROR_CODE: "OSMS-SUBSCRIPTION-0007",
    SUBS_PAYMENT_ERROR_CODE: "OSMS-SUBSCRIPTION-00021",
    CARD_UNACCEPTED_ERROR_CODE: "OSMS-SUBSCRIPTION-00022",
    INELIGIBLE_FOR_TRIAL_ERROR_CODE: "OSMS-SUBSCRIPTION-00069",
    UNABLE_TO_PROCESS_ERROR_CODE: "OSMS-SUBSCRIPTION-00070",
  },
  WEBSOURCE: {
    EXPEDIA: 'WEB-EXPEDIA',
  },
  errorMessages: {
    UNKNOWN_ERROR_MESSAGE: 'Unknown error. Please contact the support team for resolution.',
    INVALID_BANNER_MESSAGE: `Freshpass isn't available on {banner} yet, but it's coming soon! Stay tuned!`,
    CARD_CANNOT_BE_ACCEPTED_MESSAGE: 'Card cannot be accepted. Please confirm card details entered correctly or continue with another form of payment.',
    OFFER_NO_LONGER_AVAILABLE_MESSAGE: 'We\'re sorry, this offer is no longer available.',
    DEFAULT_ERROR_MESSAGE: 'Sorry, we\'re having technical difficulties and working on fixing them, please check back later.',
    INVALID_REASON_MESSAGE: "Cancellation reason is invalid.",
    CANCEL_DURING_BATCH_PROCESSING_MESSAGE: "Subscription is renewing and cannot be cancelled.",
    CANCEL_WITHIN_BUFFER_TIME_MESSAGE: "Your plan is currently being renewed and cannot be cancelled at this time.",
    SERVER_ERROR_MESSAGE: "Internal server error occured.",
    UNKNNOWN_ERROR_MESSAGE: "Unknown error. Please contact the support team for resolution.",
    SUBS_PAYMENT_ERROR_MESSAGE: "Your enrollment cannot be processed. Please update your payment method and try again.",
    CARD_UNACCEPTED_ERROR_MESSAGE: "Card cannot be accepted. Please confirm card details were entered correctly or continue with another form of payment",
  },
  freshpassOffers: {
    ltoTitle: "Exclusive Limited Time Offers",
    freshpassLTO: "FRESHPASS LTO",
    freshpassPerks: "FRESHPASS",
  },
  partnersOffers: {
    PARTNER_OFFER_TITLE_V2: "Exclusive partner savings",
    PARTNER_OFFER_SUBTITLE_V2: "Enjoy these benefits with your subscription.",
    PARTNER_OFFER_TITLE: "Partner offers",
    PARTNER_OFFER_SUBTITLE: "Subscribe to FreshPass and get access to these exclusive partnership benefits.",
    VALID_PARTNER_PROGRAM_TYPES: ["freshpass"],
    PARTNER_BANNER_FILTER: "partnerbanner",
  },
  notificationMsg: {
    activeEnrollmentSuccessMsg: 'FreshPass® is active! Perks are on their way.',
    trialEnrollmentSuccessMsg: 'FreshPass® free trial is active! Perks are on their way.',
    canceledMsg: 'Your current FreshPass plan has been cancelled effective immediately per your request.',
    cancelProcessedMsg: '{planType}: Your cancellation request has been processed',
    cancelTrialProcessedMsg: '{isTrial} - {planType}: Your cancellation request has been processed.',
    updatePlanSuccessMsg: 'Your plan has been updated successfully.',
  },
  planCard: {
    selectAction: "Select",
    SNAPAction: "See if I'm eligible",
    plusIcon: "ocsp-web-ui/dist/assets/images/plus.svg",
    moreIcon: "ocsp-web-ui/dist/assets/images/more.svg",
    greenIcon: "ocsp-web-ui/dist/assets/images/green.svg",
    checkmarkCircle: "ocsp-web-ui/dist/assets/images/checkmark-circle.svg",
    trialLabel: "-day free trial",
    creditIcon: "ocsp-web-ui/dist/assets/images/Union.svg",
    annualPlanDescription: "+Tax, Which is equal to just {price}<br/>Auto-renews{renewPrice}.",
    annualDiscountPlanDescription: "+Tax, Auto-renews{renewPrice}",
    monthlyPlanDescription: "+Tax, Auto-renews{price}.",
    SNAPPlanDescription: "For select government assistance programs.<br/> Requires yearly verification.",
    AnnualPlan: "Annual Plan",
    MonthlyPlan: "Monthly Plan",
    SNAP: "SNAP",
    annualCreditLabel: "Monthly credit - a $60 value*",
    snapNotVerified: "Not Verified",
    planDescriptionForSNAPVerified: "+Tax, Requires yearly re-verification or is automatically canceled.",
    monthlyPlanDescriptionForSNAPVerified: "+Tax, Auto-renews at {price}",
    annualOrmonthlySheerLabel: "Your eligibility for government assistance has been verified! Your plan will auto-renew at {price}",
    successColor: "success",
    snapBannerDescription: "Select government assistance programs get 50% off.",
    freshpassBenefitDescription: "Select government assistance programs get all the FreshPass benefits at just",
    campaignTitleText: "FreshPass benefits for half the price",
    planPerYear: "/year or ",
    planPerMonth: "/month.",
    monthlyDescriptionForSNAPVerified: "+Tax, Auto-renews at {price}. Requires yearly re-<br>verification or is automatically canceled",
    monthlyDescriptionForSNAPVerifiedRef: "+Tax, Auto-renews at {price}. Requires yearly re-verification or is automatically canceled",
    AnnualDescriptionForSNAPVerified: "+Tax, Requires yearly re-verification or is automatically<br>canceled.",
  },
  freshpassData: {
    freshpassTitle: "FreshPass™",
    freeTrialTitle: "Free Trial",
    freshpassManageTitle: 'Manage your FreshPass® plan',
    annualPlanText: "Annual Plan",
    manageText: "manage",
    snapKey: "snap",
    forTheFirstYearText: "for the first year",
    snapVerifiedActivationMessage: "Your eligibility for government assistance has been verified! Your plan will auto-renew at ${fee}{pricingDetail} (+tax)",
    pendingActivationStatusMessage: "Your plan will auto renew at ${pendingFee}{pendingPricingDetail} after your current plan ends.",
    suspendedGovElibilityText: "Your FreshPass plan is currently suspended. Please re-verify your ",
    govAssistanceElibilityText: "government assistance eligibility.",
    pleaseReverifyEligibilityText: "Please re-verify your eligibility.",
    snapTrialDescription: "+Tax, charged after {trial-duration}-day free trial. Annual re-verification will be required for <br> continued access to discounted subscription price.",
    snapNonTrialDescription: "+Tax. Annual re-verification will be required for continued access to discounted subscription price.",
    fpBenefitsDescription: "Savor next-level savings with your FreshPass subscriber-only benefits. ",
    lifeTimeSavingsText: "Lifetime Freshpass Savings",
    hoursSavedText: "Hours Saved",
    continueSubscription: "Continue FreshPass Subscription",
    noThanksContinueShoping: "No Thanks. Continue Shopping",
    suspendedSecondaryUserPrompt: "Your FreshPass® plan is currently disabled due to an issue with your payment method. Please contact the primary FreshPass® account holder to update the payment method.",
    suspendedSecondaryUserAndGovAssistancePrompt: 'Your FreshPass® plan is currently disabled. Please contact the primary FreshPass® account holder to re-verify their government assistance eligibility.',
    lifeTimeSavingsTooltipDisclaimer: "Total savings are calculated starting on 6/22/2022. If you subscribed to FreshPass™ after this date, your total savings are calculated starting on your subscription date. Total savings calculation is based on discounts to grocery delivery fees and redemption of perks listed on the FreshPass™ subscription page."
  },
  subscribeData: {
    step2HeaderTitle: '',
    redirectedTo: "You will be redirected to",
    shop: "Shop",
    likeToProceed: "you like to proceed?",
    completed: "Completed",
    step: "Step",
    step1: "Step 1",
    step2: "Step 2",
    step3: "Step 3",
    currentStep: "Current step",
    agreeTo: "I agree to the",
    cancel: "Cancel",
    freshPass: "FreshPass",
    zipHeading: "Savor unlimited free delivery* & next-level savings",
    zipAltText: "Freshpass logo",
    zipCtaFreeTrial: "Try it FREE for {trial-duration} days",
    zipCtaRegular: "Get started",
    zipExtendedTrial: "plus a {trial-duration}-day free trial",
    noDeliveryZipDescription: "Apologies, FreshPass is not available in your area.",
    selectPlanZipSolicitationLabel: "Enter ZIP Code to get started",
    selectPlanFreeTrialMessage: "Get started by selecting a plan below:",
    selectPlanRegularMessage: "Save extra money on your monthly deliveries!",
    annualPlanDefaultRibbon: "Best Value",
    annualPlanDescription: "+Tax, Which is equal to just {price}!<br />Auto-renews{renewPrice}.",
    snapMonthlyPlanDescription: "+Tax, Billed monthly!<br />Requires yearly verification or is automatically cancelled. Discount plan available for up to 24 months",
    snapMonthlyPlanDescriptionSpacing: "+Tax, Billed monthly!<br />Requires yearly verification or is automatically cancelled.<br />Discount plan available for up to 24 months",
    annualPlanCtaFreeTrial: "Start {trial-duration}-day Free Trial",
    annualPlanCtaRegular: "Select",
    annualPlanIsDefaultPlan: "true",
    monthlyPlanDefaultRibbon: "Save 35%",
    monthlyPlanDescription: "+Tax, Billed monthly.<br /> Auto-renews{price}.",
    monthlyPlanDescription2: "+Tax, Auto-renews at {price}.",
    discountRibbonLabel: "NA",
    paymentNote: "Note: Add or select a credit or debit card for your subscription. Your payment card will not be charged until your free {trial-duration}-day trial has ended.",
    paymentNoteNoTrial: "Note: Your payment card will be charged immediately when confirming sign up for FreshPass.",
    trialTaxMessage: "+Tax, Charged after {trial-duration}-day free trial. Auto-renews at {annual-plan-price}{conjunction}{monthly-plan-price}.",
    taxMessage: "+Tax, Auto-renews at {annual-plan-price}{conjunction}{monthly-plan-price}.",
    bannerPaymentTermsMsg: "FreshPass",
  },
  subscriptionAuthoringFields: {
    paymentFreeTrialButtonCTA: "Start Free Trial",
    paymentButtonCTA: "Sign Up",
    thankYouPaymentDetailsLabel: "Payment Details",
    thankYouSubscriptionLabel: "Current Subscription Plan ",
    thankYouMsg: "You're all set! Thank you for registering, your FreshPass membership is now active. ",
    changePlanLabel: "Manage Plan",
    changePlanLink: "/content/www/safeway/en/delivery-subscription/manage-delivery-subscription.html",
    updatePaymentLabel: "Update Payment Method",
    updatePaymentLink: "/content/www/safeway/en/account/payment.html",
    showNowButtonLabel: "Shop Now",
    showNowButtonLink: "/",
    selectPlanChangeZipcode: "Change ZIP Code",
    selectPlanTitle: "Choose your plan",
    selectPlanHeading: "Unlimited Delivery is available in",
    freshpassHeading: "Select a plan",
    freshpassHeading2: "Select a government assisted plan",
    selectPlanTaxes: "(+ tax)",
    selectPlanTaxesSnap: "(+ any applicable taxes)",
    accountTitle: "Set up your account",
    freshPassNoDelivery: "We're sorry, delivery with FreshPass is not available in ",
    freshPassBenefitsTitle: "FreshPass Benefits",
    paymentTitle: "Payment details",
    restrictedZipDescription: "Apologies, FreshPass® is not available in your area",
    sisterBannerDescription: "FreshPass is available at our sister store",
    paymentAuthorizeMsgAferPlanOne: ". I consent to and authorize storage of my debit and/or credit card data and authorize charges to such card for purchases, subscription fees after the",
    paymentAuthorizeMsgAferPlanTwo: "-day free trial and for subsequent auto-renewals until I cancel.",
    terms: "terms",
    paymentAuthorizeMsgCurrentPlan:
      ". I consent to and authorize storage of my debit and/or credit card data and authorize charges to such card for current subscription fees and future auto-renewals until I cancel.",
    subscriptionPlan: "Subscription Plan",
  },
  manage: {
    CURRENT_PLAN_ICON: "ocsp-web-ui/dist/assets/images/5-credit-badge-small.svg",
    PAYMENT_METHOD: "Payment Method",
    CURRENT_PAYMENT_METHOD: "Current Payment Method",
    ADD_A_CREDIT_OR_DEBIT_CARD_FOR_YOUR_SUBSCRIPTION: "Add a credit or debit card for your subscription",
    UPDATE_PAYMENT_METHOD: "Update Payment Method",
    ADD_NEW_CARD: "Add New Card",
    EXPIRED: "Expired",
    CONFIRM_CVV: "Confirm CVV",
    CHANGE_PLAN: "Change Plan",
    SWITCH_TO_FORUPLUS: "Switch to forU+",
    B4UPLUS_MONTHLY_MSG: "FreshPass is changing to {banner} forU+. To keep enjoying delivery and pickup savings, switch to the annual plan at " + B4UPLUS_ANNUAL_PRICE + " before your renewal on {renewalDate}.",
    B4UPLUS_ANNUAL_MSG: "FreshPass is moving to {banner} forU+, now " + B4UPLUS_ANNUAL_PRICE + ". You’ll auto-renew on {renewalDate}—keep enjoying your current benefits until then.",
    B4UPLUS_LEARN_MORE_URL: "/4uplus/change-management.html",
    B4UPLUS_SWITCH_SUCCESS_MSG: "Great! You’ll continue to receive FreshPass benefits. Your {banner} forU+ plan will auto renew at " + B4UPLUS_ANNUAL_PRICE + " after your current plan ends.",
    // The leading "$" is a literal dollar sign in the displayed copy (double quotes, not a JS template literal); {currentPrice} etc. are runtime-replaced tokens.
    B4UPLUS_CURRENT_UNTIL_EXPIRY: "${currentPrice}{pricingDetail} (+tax) until {currentExpiryDate}",
    B4UPLUS_ANNUAL_STARTS_MSG: "Your annual plan will start on {renewalDate}",
    SNAP_AUTO_CANCEL_MSG: "Your plan will be automatically canceled on {cancelDate}",
    Cancel_FreshPass: "Cancel FreshPass",
    YOUR_PLAN_CAN_NOT_BE_CHANGED_AGAIN_UNTIL_THE_CURRENT_UPDATE_IS_COMPLETE: "Your plan can not be changed again until the current update is complete.",
    MONTHLY: "Monthly",
    ANNUAL: "Annual",
    ANNUAL_PLAN: "Annual Plan",
    MONTHLY_PLAN: "Monthly Plan",
    DISCOUNTED_PLAN_BEGINS_MSG: "Discounted {planType} plan begins</br>{renewalDate}",
    CURRENT_PLAN_BEGINS_MSG: "{planType} plan begins {renewalDate}",
    CURRENT_PLAN_RENEWS_MSG: "Renews {renewalDate}",
    TRIAL: "TRIAL",
    ACTIVE: "ACTIVE",
    SUSPENDED: "Suspended",
    PENDING_ACTIVATION: "PENDING_ACTIVATION",
    PROCESSING: "PROCESSING",
    PENDING_CANCELLATION: "PENDING_CANCELLATION",
    CHANGE_PLAN_CON: "CHANGE_PLAN",
    FREE_TRIAL_ENDS_ON_MSG: "Free trial ends on {currentPlanExpiryDate}",
    FREE_TRIAL: "Free Trial",
    MONTHLY_SUBSCRIBER: "Monthly Subscriber",
    ANNUAL_SUBSCRIBER: "Annual Subscriber",
    CANCELLED: "CANCELLED",
    PLAN_PER_YEAR: "/year",
    PLAN_PER_MONTH: "/month",
    TAX: "(+tax)",
    CURRENT_PLAN_PRICE_DETAILS: "${currentPrice}{pricingDetail} (+tax)",
    AUTO_RENEWS_AT: "Auto-renews at $",
    AUTO_RENEWS_AT_REGULAR_PLAN_PRICE: "Auto-renews at ${regularPlanPrice}{regularPricingDetails}.",
    AUTO_RENEWS_AT_REGULAR_PLAN_PRICE_TAX: "Auto-renews at ${regularPlanPrice}{regularPricingDetails} (+tax).",
    AUTO_RENEWS_AT_CURRENT_FEE_TAX: 'Auto-renews at ${currentFee}{currentPricingDetails} (+tax).',
    AUTO_RENEWS_AT_PENDING_FEE: 'Auto-renews at ${pendingFee}{pendingPricingDetails}.',
    AUTO_RENEWS_AT_PENDING_FEE_FOR_FIRST_YEAR: 'Auto-renews at ${pendingFee}{pendingPricingDetails}.',
    AUTO_RENEWS_AT_PENDING_FEE_TAX: 'Auto-renews at ${pendingFee}{pendingPricingDetails} (+tax).',
    CURRENT_PLAN_UNTIL_EXPIRY_DATE: '${currentPrice}{pricingDetail} until {currentExpiryDate} (+tax)',
    UNTIL: "until",
    CURRENT_PLAN_ENDS_MSG: "Ends {expiryDate}",
    DISCOUNT_PLAN_BEGINS_MSG: "{discounted} {planType} begins </br> {renewalDate}",
    PLAN_BEGINS_MSG: "{planType} plan begins {renewalDate}",
    VIP_CUSTOMER_SERVICE_PHONE_LINE: "VIP Customer Service Phone Line",
    FRESH_PASS_SUBSCRIBERS_RECEIVE_QUICK_CUSTOMER_SERVICE_FROM_A_DEDICATED_SUPPORT_TEAM: "FreshPass subscribers receive quick customer service from a dedicated support team.",
    Num_855_767_2545: "855-767-2545",
    SHOP_NOW: "Shop Now",
    DELIVERY_SUBSCRIPTION: "DELIVERY_SUBSCRIPTION",
    NOTIFY_MESSAGE: "notifyMessage",
    F9_B88_F7_E_5_F54_4536_AFC0_8_B080_D189_CDF: "F9B88F7E-5F54-4536-AFC0-8B080D189CDF",
    YOUR_FRESH_PASS_PLAN_IS: "Your FreshPass plan is currently disabled due to an issue with your payment method. Please update your payment method.",
    YOUR_CURRENT_FRESH_PASS_PLAN: "Your current FreshPass plan has been <b>cancelled</b> per your request, you can still continue to use FreshPass until",
    YOUR_CURRENT_FRESH_PASS_PLAN_HAS_BEEN: "Your current FreshPass plan has been <b>cancelled</b> per your request. Your Free Trial ends on",
    FRESH_PASS_PLAN_WILL_BECOME_EFFECTIVE_AFTER_THE_CURRENT_PLAN_EXPIRES_ON: "FreshPass plan will become effective after the current plan expires on ",
    YOUR_FREE_TRIAL_ENDS_ON: "Your free trial ends on ",
    YOU_LL_BE_AUTOMATICALLY_ENROLLED_INTO_THE: "you’ll be automatically enrolled into the ",
    TRIAL_STATUS_MESSAGE: "Your free trial ends on {expirationDate}, you’ll be automatically enrolled into the <b>{expirationPlanType}</b> FreshPass plan on {renewalDate}.",
    FRESH_PASS_PLAN_ON: "FreshPass plan on ",
    YOU_CAN_STILL_CONTINUE_TO_USE_FRESH_PASS_UNTIL: "You can still continue to use FreshPass until ",
    YOUR: "Your $",
    MANAGE_MY_PLAN: "Manage My Plan",
    FRESHPASS: "FreshPass<sup>®</sup>",
    NEED_HELP: "Need Help?",
    HERE: "here",
    RESTRICTIONS_APPLY_FULL_PROGRAM_TERMS_CONDITIONS: "Restrictions apply. Refer to Full program terms & conditions below.",
    RESTRICTIONS_APPLY_FULL_PROGRAM_TERMS_CONDITIONS_LINK: "Restrictions apply. Refer to Full program terms & conditions",
  },
  confirmation: {
    SAVING_SUMMARY_MODAL: "saving-summary-modal",
    ESTIMATE_ANNUAL_SAVINGS_TERMS: "estimate annual savings terms",
    TERMS_INFO_MODAL_TEXT: "termsInfoModalText",
    SAVINGS_SUMMARY: "Savings Summary",
    updateBanner: {
      TITLE: "FreshPass is updating",
      BODY_MONTHLY: "FreshPass is changing to {banner} forU+. To keep enjoying delivery and pickup savings, switch to the annual plan at " + B4UPLUS_ANNUAL_PRICE + " before your renewal date.",
      BODY_ANNUAL: "Your FreshPass plan is changing to the new {banner} forU+ plan with a lower annual fee. You can continue enjoying your FreshPass benefits until then.",
      SWITCH_PLAN: "Switch plan",
      SWITCH_PLAN_URL: "/freshpass/manage/change.html",
      LEARN_MORE: "Learn more",
      LEARN_MORE_URL: "/4uplus/change-management.html",
      CLOSE_ARIA: "close",
      ICON_ALT: "information",
    },
    FRESH_PASS_ANNUAL_SUBSCRIBERS_COULD_SAVE_UP_TO_425_PER_YEAR: "FreshPass annual subscribers could save up to $425 per year",
    ESTIMATED_ANNUAL_SAVINGS: "Estimated Annual Savings",
    SAVED_AND_COUNTING: " saved, and counting!",
    CONGRATS_YOU_VE_SAVED: "Congrats! You've saved ",
    WITH_FRESH_PASS: " with FreshPass",
    WE_HOPE_YOU_RE_ENJOYING_THE_PERKS_AND_THE_SAVINGS: "We hope you're enjoying the perks and the savings!",
    OK_GOT_IT: "Ok, Got It",
    TRIAL: "trial",
    PROCESSING: "processing",
    ANNUAL_PLAN: "Annual Plan",
    ANNUAL: "annual",
    MONTHLY: "monthly",
    YOUR_FRESHPASS_PERKS: "Your FreshPass Perks",
    FRESHPASS_PERKS: "FreshPass Perks",
    YOUR_FRESH_PASS_BENEFITS: "Your FreshPass Benefits",
    ACCOUNT_SUSPENDED: "Account Suspended",
    YOUR_FRESH_PASS_PLAN_IS_CURRENTLY_DISABLED_DUE_TO_AN_ISSUE_WITH_YOUR: "Your FreshPass plan is currently disabled due to an issue with your payment method. Please update your",
    PAYMENT_METHOD: "payment method.",
    WELCOME_TO_FRESH_PASS_THANKS_FOR_SIGNING_UP_YOUR_PERKS_ARE_ON_THE_WAY_IF: "Welcome to FreshPass! Thanks for signing up, your perks are on the way. If you've redeemed them all, check back soon.",
    DON_T_MISS_OUT_ON_FREE_SAVINGS: "Don't miss out on free savings!",
    FRESH_PASS_ANNUAL_PLAN_SUBSCRIBERS_GET_A_5_CREDIT_EVERY_MONTH: "FreshPass annual plan subscribers get a $5 credit every month",
    AVAILABLE_TO_YOU: "Available to you",
    UPGRADE_NOW: "Upgrade Now",
    ADDED: "Added",
    COUPON_CLIP_SESSION_IS_EXPIRED_RELOADING_THE_PAGE: "Coupon clip session is expired. Reloading the page.",
    IN_STORE: "In-store",
    invalidReasonCode: "Cancellation reason is invalid.",
    cancelDuringBatchProcess: "Subscription is renewing and cannot be cancelled.",
    cancelWithinBufferTime: "Your plan is currently being renewed and cannot be cancelled at this time.",

    serverError: "Internal server error occured.",
    unknownError: "Unknown error. Please contact the support team for resolution.",
    subsPaymentError: "Your enrollment cannot be processed. Please update your payment method and try again.",
    subsDefaultError: "Sorry, we're having technical difficulties and working on fixing them, please check back later.",
    cardUnacceptedError: "Card cannot be accepted. Please confirm card details were entered correctly or continue with another form of payment",
    PARTNERS: "Partners",
    FRESHPASS_SUBSCRIPTION_CONFIRMATION: "Freshpass - Subscription Confirmation",
    PRTNRS: "prtnrs",
    NO_PRTNRS: "no-prtnrs",
    CONFIRMATION: "confirmation",
    WELCOME: "Welcome",
    ALBERTSONS: "albertsons",
    STARMARKET: "starmarket",
    PAVILIONS: "pavilions",
    ERROR_FETCHING_LOGO_LIST: "error fetching logo list",
    SHAWS: "shaws",
    MASK: "mask",
    FRESHPASSTILEPARSYS: "freshpasstileparsys",
    ERROR_FETCHING_EXPERIENCE_FRAGMENT: "error fetching experience fragment",
    CLASS: "milestone-modal",
    backdrop: "static",
    ariaLabelledBy: "modal milestone dropdown",
    ariaDescribedby: "milestone dropdown",
    timeToShow: "timeToShow",
    ERROR_FETCHING_PARTNERS_DATA: "error fetching partners data",
    notifyMessage: "notifyMessage",
    OFFER_DETAILS: "Offer Details",
    defaultSuspendedErrorMessage: 'Your FreshPass plan is currently disabled due to an issue with your payment method. Please update your',
    defaultSuspendedErrorLinkText: 'payment method'

  },
  notificationModal: {
    promotionExpired: "This promotion has expired",
    viewOffers: "View current offers",
    weAreSorry: "we're sorry, this",
    noLongerAvailable: "is no longer available",
    inEligibleForTrialErrorProps: {
      errorCode: "OSMS-SUBSCRIPTION-00069",
      title: 'Your FreshPass free trial ended',
      message: 'Looks like you’ve already experienced the perks of the free trial. If you continue, you will be charged for the FreshPass membership.',
      primaryBtnText: 'Continue with FreshPass',
      enableBackButton: true,
      backButtonText: 'Go back',
    },
    unableToProcessErrorProps: {
      errorCode: "OSMS-SUBSCRIPTION-00070",
      title: 'We’re unable to process your order.',
      message: 'You have not been charged and we apologize for any inconvenience. If you would like future assistance, please call Customer Support at 855-799-4584.',
      primaryBtnText: 'Close',
      enableBackButton: false,
      backButtonText: '',
    },
  },
  personalizedSavings: {
    savedSoFar: "saved so far with FreshPass",
  },
  savingsSummary: {
    existingDeliveryEngagedText:
      "Based on average delivery orders for existing delivery engaged FreshPass subscribers from 4/1/2021 to 9/23/2021 utilizing non-prime order time slots (delivery fees vary – value based on existing slot utilization of subscribers). Savings calculation also includes $20 savings for first time members on first $75 online purchase, monthly perk values, and product category savings that annual subscribers enjoy. Product savings value is calculated based on existing subscriber data",
    close: "CLOSE",
  },
  termsAndConditions: {
    forMoreInfo: "For more information, see our",
    faq: "FAQ",
    termsNconditions: "Terms and Conditions",
},
  cancelFreshPass: {
    cancelPlan: "Cancel Plan",
    reasonForCancellationSelect: "Reason for Cancellation (select one):",
    reasonForCancellation: "Reason for Cancellation",
    additionalFeedback: "Additional Feedback",
    noOfCharacters: "No. of characters 0 / 400",
    cancelUserText: "We hate to see you go. Are you sure you want to end your subscription?",
    yesCancelPlan: "Yes, Cancel Plan",
    keepPlan: "No, Keep Plan",
    discountPlanTitle: "Interested in a one-time discount?",
    discountPlanSecondTitle: "We appreciate you—and want to give you a little gift to show it.",
    discountPlanBtnDesc: "Yes, I'm interested",
    discountPlanCancelBtnDesc: "Continue to cancel",
    benefitsBtnDesc: "Get discounted price",
    benefitsSplOffer: "Special Offer",
    benefitsRetentionPlanAnnual: "Enjoy one year of FreshPass annual for",
    benefitsRetentionPlanAnnualForSrOnly: "Enjoy one year of FreshPass annual for new price",
    benefitsTaxInfo: "+Tax,",
    benefitsRenewMessage:"+Tax, Auto-renews {price}",
    infoMsgForFPCancelRefundNotEligible:"If you cancel today, you keep your FreshPass benefits until ",
    infoMsgForFPCancelRefundEligible: "Because you have not used the FreshPass service and are canceling within 15 days, your subscription fee will be refunded",
    beforeYouGoMessage: 'Before you go, please tell us why you’re not keeping your FreshPass subscription.'
  },
  changePlanSummary: {
    offYourSubscription: "50% off your subscription",
  },
  annualPlan: {
    taxText: "+Tax, Which is equal to $",
    monthText: "/month!",
    renewelText: "Auto-renews at",
    selectAnnual: "Select Annual",
    currentPlan: "Current Plan",
  },
  changePlan: {
    govtAssisted: " government assisted",
    month: "month",
    year: "year",
    trial: "Trial",
    changePlanText: "Change plan",
    planTextOne: " If you select the",
    plan: "plan, ",
    planTextTwo: "it will become effective after the current plan expires on",
  },
  monthlyPlan: {
    taxMonthly: "+Tax, Billed monthly!",
    selectMonthly: "Select Monthly",
    months: "months",
    autoRenew: "Auto-renews.",
  },
  cancelFreshpassSummary: {
    monthlyFreshpass: "Monthly FreshPass",
    annualFreshPass: "Annual FreshPass",
  },
  paymentModel: {
    API_FAIL_ERROR_MESSAGE: "Sorry, we're having technical difficulties and working on fixing them, please check back later.",
    UNKNOWN_ERROR_MESSAGE: "Unknown error. Please contact the support team for resolution.",
    update: "Update",
    FORUPLUS_PROMO_TITLE: "forU+, a simple way to save",
    FORUPLUS_PROMO_BODY: "Unlimited free delivery, free Flash™ pickup, and $2 Flash delivery.",
  },
  prechurn: {
    retentionDiscountType: 'retentionannual',
    retentionCampaignType: 'retention',
    successStatusMessage: 'Great! You’ll continue to receive FreshPass benefits. Your plan will auto renew at ${pendingFee}{pendingPricingDetail} after your current plan ends.',
    discountPlanBeginsMessage: "Discounted {pendingSubscriptionType} plan begins</br>{renewalDate}"
  },
  partnersBanners: {
    bannerHeading: "Select your preferred store to claim your discount",
    bannerTitle: "Select your store, create an account or sign in. Then subscribe to FreshPass to claim your discount.",
    joinFreshpass: "Join FreshPass",
    FAQ: "Frequently Asked Questions",
    termsAndCondition: "Terms & Conditions",
    continue: "Continue",
  },
  partnersLanding: {
    ERROR_DISCOUNT_TITLE: "Sorry, this discount is no longer available.",
    ERROR_DISCOUNT_MESSAGE: "Oops! This offer is either invalid or has expired. Sign up now and start receiving FreshPass benefits today.",
    ERROR_MISSING_BANNERS_TITLE: "Sorry, this page is currently down.",
    ERROR_MISSING_BANNERS_MESSAGE: "Oops! Please check back soon or sign up now and start receiving FreshPass benefits today.",
    DISCOUNT_ERROR: "DISCOUNT_ERROR",
    BANNER_ERROR: "BANNER_ERROR",
  },
  updatePayments: {
    API_FAIL_ERROR_MESSAGE: "Sorry, we're having technical difficulties and working on fixing them, please check back later.",
    paymentUpdatedMsg: "You're  all set! Your payment method has been updated.",
  },
  snapHowItWorks: {
    benefitsTitle: "3 Easy Steps to Sign Up!",
    howItWorksHeading: "Verify your eligibility",
    howItWorksText: "Complete the verification process in your account settings.",
    paymentHeading: "Enter a payment method",
    freeTrailEndText: "When your free trial ends, this payment method will be used for any recurring monthly charges.",
    enjoyBenefits: "Enjoy the benefits!",
    freeDeliveryText: "Get free delivery and all the other exclusive perks.",
  },
  snapBenefits: {
    benefitsTitle: "Your FreshPass benefits",
    termsAndConditions: "Restrictions apply. Refer to FreshPass Terms & Conditions below.",
    dontQualifyText: "Don't qualify? View our other plans.",
    childList: "childList",
  },
}

export const properties = {
  externalDeps: {
    "lazyJs": ["/ocsp-web-ui/dist/assets/js/signifyd-session-handler.js"],
  },
  oktaRedirectUri: "/osar/msi",
  oktaRedirectUriImplict: "/osar/msi/implicit/callback",
  oktaRedirectUriConfirmation: "/erums/order/confirmation/implicit/callback",
  erumsRedirectCookieName: "erums-from-uri",
  storeResolverZipcodeToShoppingEndPoint: "/abs/qapub/xapi/storeresolver/zipcodetoshopping",
  clientBannerHeader: "banner",
  headerStoreId: "",
  headerFulfillment: "instore",
  storeId: "",
  SVG: '.svg',
  cookiesOneTrustConsentEle: "onetrust-consent-sdk",
  tooltips: {
    personalizedSavings: {
      title: "FreshPass™ Savings",
      text: "Total savings are calculated starting on 6/22/2022. If you subscribed to forU+ after this date, your total savings are calculated starting on your subscription date. Total savings calculation is based on discounts to grocery delivery fees and redemption of perks listed on the forU+ subscription page.",
    },
    estimatedTotal: {
      title: "Estimated Total",
      text:
        "<b>You will pay the prices, fees, and other charges, and receive the promotions, offers, discounts, and savings applicable to your order on the date of scheduled delivery/pickup.</b>" +
        " The estimated total includes applicable delivery fees but does not include coupons clipped, buy one get one deals, club card, " +
        "Schedule & Save and promo code discounts, certain applicable taxes, bottle/can deposit, bag/other fees unless otherwise noted, the cost of substituted items, " +
        "or reductions from out of stock items removed from your order. The final amount shown on your receipt at the time of delivery or pickup will reflect these adjustments. " +
        "A hold will be placed on your debit/ credit card for this estimated total and will reduce the amount available to spend from your account. " +
        "Your bank may take up to 5 business days after delivery/pickup to release the hold and display your final charge.",
      textV1: "",
      warning: false,
    },
    estimatedSubTotal: {
      title: "Estimated SubTotal",
      text:
        "<b>You will pay the prices, fees, and other charges, and receive the promotions, offers, discounts, and savings applicable to your order on the date of scheduled delivery/pickup.</b>" +
        " The estimated sub total does not include coupons clipped, buy one get one deals, club card, Schedule & Save and promo code discounts, " +
        "delivery fees if applicable, certain applicable taxes, bottle/can deposit, bag/other fees unless otherwise noted, " +
        "the cost of substituted items, or reductions from out of stock items removed from your order.",
      textV1: "",
      warning: false,
    },
    oneTimePurchaseItems: {
      title: "One-Time Purchase Items",
      text: '<b>Use "Add Items" to include one-time purchase items in an already scheduled delivery/pickup order. One-time purchase items will not be added to future Schedule & Save orders.',
      textV1: "",
      warning: false,
    },
    deliveryFee: {
      title: "What is a Delivery Fee?",
      text: "<div>" + ' <div class="verticalSpace-5"></div>' + "A delivery fee helps us operate our fleet of trucks that transports your order from our facilities to your address." + "</div>",
      warning: false,
    },
    udcInfo: {
      title: "Unlimited Delivery Club",
      text:
        '<p class="mb-2">Save money when you signup for unlimited deliveries for one low fee!<p>' +
        "<br/>" +
        "Sign up for one of our Unlimited Delivery Club Service plans and we will waive the delivery fee on any order over $30 for the selected duration. You can choose a monthly plan for $14.99 or an annual plan for $99. (The annual plan averages out to just $8.25/month.)",
      warning: false,
    },
    udcTerms: {
      title: "Unlimited Delivery Club Terms",
      text:
        '<p class="mb-2">You agree to consent and authorize storage of your debit and/or credit card data' +
        "and authorize charges to such card for subscription fees after the 30-day free trial and" +
        "for subsequent auto-renewals until you cancel." +
        "</p>",
      warning: false,
    },
    udcTermsFreeTrial: {
      title: "Unlimited Delivery Club Terms",
      text: "I consent to and authorize storage of my debit and/or credit card data and authorize charges to such card for subscription fees after the 30-day free trial and for subsequent auto-renewals until I cancel.",
      warning: false,
    },
    udcTermsNonFreeTrial: {
      title: "Unlimited Delivery Club Terms",
      text: "I consent to and authorize storage of my debit and/or credit card data and authorize charges to such card for current subscription fees and future auto-renewals until I cancel.",
      warning: false,
    },
    estimatedTax: {
      title: "Estimated Taxes & Fees",
      text: "The Estimated Taxes and Fees shown include your delivery fee (if applicable), any applicable taxes, bottle/can deposits, and bag/other fees. Your final receipt may reflect adjustments to these taxes, deposits and fees due to changes in your order, including but not limited to the availability or expiration of promotions, offers, discounts, and savings, the final cost of items sold by weight, item substitutions, or out of stock items removed from your order.",
    },
  },
  bannerConfiguration: {
    tomthumb: {
      banner: "Tom Thumb",
      loyaltyPgmName: "Reward Card",
      promoDescription: "Reward Card Price",
      estimatedSavings: "Est. Reward Card Savings",
      adaEstimatedSavings: "Estimated Reward Card Savings",
      logoUrls: {
        color: "TomThumb_RGB.svg",
        white: "TomThumb_W.svg",
      },
    },
    albertsons: {
      banner: "Albertsons",
      loyaltyPgmName: "On Sale",
      promoDescription: "On Sale Now",
      estimatedSavings: "Est. On Sale Savings",
      adaEstimatedSavings: "Estimated On Sale Savings",
      logoUrls: {
        color: "Albertsons_RGB.svg",
        white: "Albertsons_W.svg",
      },
    },
    vons: {
      banner: "Vons",
      loyaltyPgmName: "Club Card",
      promoDescription: "Club Card Price",
      estimatedSavings: "Est. Club Card Savings",
      adaEstimatedSavings: "Estimated Club Card Savings",
      logoUrls: {
        color: "Vons_RGB.svg",
        white: "Vons_W.svg",
      },
    },
    safeway: {
      banner: "Safeway",
      loyaltyPgmName: "Club Card",
      promoDescription: "Club Card Price",
      estimatedSavings: "Est. Club Card Savings",
      adaEstimatedSavings: "Estimated Club Card Savings",
      logoUrls: {
        color: "Safeway_RGB.svg",
        white: "Safeway_W.svg",
      },
    },
    randalls: {
      banner: "Randalls",
      loyaltyPgmName: "Remarkable Card",
      promoDescription: "Remarkable Card Price",
      estimatedSavings: "Est. Remarkable Card Savings",
      adaEstimatedSavings: "Estimated Remarkable Card Savings",
      logoUrls: {
        color: "Randalls_RGB.svg",
        white: "Randalls_W.svg",
      },
    },
    pavilions: {
      banner: "Pavilions",
      loyaltyPgmName: "Club Price",
      promoDescription: "Club Price",
      estimatedSavings: "Est. Club Price Savings",
      adaEstimatedSavings: "Estimated Club Price Savings",
      logoUrls: {
        color: "Pavilions_RGB.svg",
        white: "Pavilions_W.svg",
      },
    },
    jewelosco: {
      banner: "Jewel-Osco",
      loyaltyPgmName: "Sale Price",
      promoDescription: "Sale Price",
      estimatedSavings: "Est. Sale Price Savings",
      adaEstimatedSavings: "Estimated Sale Price Savings",
      logoUrls: {
        color: "JewelOsco_Vert_Oval_RGB.svg",
        white: "JewelOsco_Vert_Oval_W.svg",
      },
    },
    carrsqc: {
      banner: "Carrs",
      loyaltyPgmName: "Club Card",
      promoDescription: "Club Card Price",
      estimatedSavings: "Est. Club Card Savings",
      adaEstimatedSavings: "Estimated Club Card Savings",
      logoUrls: {
        color: "Carrs_RGB.svg",
        white: "Carrs_W.svg",
      },
    },
    acmemarkets: {
      banner: "ACME",
      loyaltyPgmName: "Sale Price",
      promoDescription: "Sale Price",
      estimatedSavings: "Est. Sale Price Savings",
      logoUrls: {
        color: "Acme_RGB.svg",
        white: "Acme_W.svg",
      },
    },
    shaws: {
      banner: "Shaws",
      loyaltyPgmName: "Sale Price",
      promoDescription: "Sale Price",
      estimatedSavings: "Est. Sale Price Savings",
      logoUrls: {
        color: "Shaws_RGB.svg",
        white: "Shaws_W.svg",
      },
    },
    starmarket: {
      banner: "Star Market",
      loyaltyPgmName: "Sale Price",
      promoDescription: "Sale Price",
      estimatedSavings: "Est. Sale Price Savings",
      logoUrls: {
        color: "StarMarket_RGB.svg",
        white: "StarMarket_W.svg",
      },
    },
    kingsfoodmarkets: {
      banner: "Kings",
      loyaltyPgmName: "Sale Price",
      promoDescription: "Sale Price",
      estimatedSavings: "Est. Sale Price Savings",
      logoUrls: {
        color: "Kingsfoodmarkets_RGB.svg",
        white: "Kingsfoodmarkets_W.svg",
      },
    },
    balduccis: {
      banner: "Balduccis",
      loyaltyPgmName: "Sale Price",
      promoDescription: "Sale Price",
      estimatedSavings: "Est. Sale Price Savings",
      logoUrls: {
        color: "Balduccis_RGB.svg",
        white: "Balduccis_W.svg",
      },
    },
    andronicos: {
      banner: 'Andronico\'s',
    },
    haggen: {
      banner: 'Haggen',
    },
    shopamigos: {
      banner: 'Amigos',
    },
    shopmarketstreet: {
      banner: 'Market Street',
    },
    shopunitedsupermarkets: {
      banner: 'United Supermarkets',
    },
    shopalbertsonsmarket: {
      banner: 'Albertsons Market',
    },
  },
  PromoTypeDescription: {
    BOGO: "Buy 1 Get 1 Free",
    B2G1: "Buy Two Get 1 Free",
  },
  timer: {
    lastLoginAllowedTime: 45,
  },
  summaryTexts: {
    sumamrySnapText: "SNAP Eligible Item Total",
    sumamryNonSnapText: "Non-SNAP Eligible Item Total",
  },
  bannerMapper: {
    safeway: "SAFEWAY",
    vons: "VONS",
    randalls: "RANDALLS",
    tomthumb: "TOMTHUMB",
    acmemarkets: "ACMEMARKETS",
    carrsqc: "CARRSQC",
    jewelosco: "JEWELOSCO",
    albertsons: "ALBERTSONS",
    starmarket: "STARMARKET",
    shaws: "SHAWS",
    pavilions: "PAVILIONS",
    kingsfoodmarkets: "KINGSFOODMARKETS",
    balduccis: "BALDUCCIS",
  },
  bannerLogoMapper: {
    safeway: "/content/dam/logos/Safeway_RGB.svg",
    vons: "/content/dam/logos/Vons_RGB.svg",
    randalls: "/content/dam/safeway/images/logos/Randalls_RGB.svg",
    tomthumb: "/content/dam/safeway/images/logos/TomThumb_RGB.svg",
    acmemarkets: "/content/dam/logos/Acme_RGB.svg",
    carrsqc: "/content/dam/logos/Carrs_RGB.svg",
    jewelosco: "/content/dam/safeway/images/logos/JewelOsco_Vert_Oval_RGB.svg",
    albertsons: "/content/dam/safeway/images/logos/Albertsons_RGB.svg",
    starmarket: "/content/dam/logos/StarMarket_RGB.svg",
    shaws: "/content/dam/logos/Shaws_RGB.svg",
    pavilions: "/content/dam/logos/Pavilions_RGB.svg",
    kingsfoodmarkets: "/content/dam/logos/Kingsfoodmarkets_RGB.svg",
    balduccis: "/content/dam/logos/Balduccis_RGB.svg",
    shopamigos: "https://images.albertsons-media.com/is/image/ABSStage/nav-shopamigos-logo",
    shopmarketstreet: "https://images.albertsons-media.com/is/image/ABSStage/nav-shopmarketstreet-logo",
    shopunitedsupermarkets: "https://images.albertsons-media.com/is/image/ABSStage/nav-shopunitedsupermarkets-logo",
    shopalbertsonsmarket: "https://images.albertsons-media.com/is/image/ABSStage/nav-shopalbertsonsmarket-logo",

  },
  invalidReasonCode: "Cancellation reason is invalid.",
  cancelDuringBatchProcess: "Subscription is renewing and cannot be cancelled.",
  cancelWithinBufferTime: "Your plan is currently being renewed and cannot be cancelled at this time.",
  serverError: "Internal server error occured.",
  cardRefundError: "We're sorry, we were unable to cancel your subscription. Please call our Customer Support team at {NUM} so we can help resolve it.",
  unknownError: "Unknown error. Please contact the support team for resolution.",
  subsPaymentError: "Your enrollment cannot be processed. Please update your payment method and try again.",
  subsDefaultError: "Sorry, we're having technical difficulties and working on fixing them, please check back later.",
  cardUnacceptedError: "Card cannot be accepted. Please confirm card details were entered correctly or continue with another form of payment",
  freshpassBenefits: {
    freshpass_tile_item__854420891: {
      fpImage: "ocsp-web-ui/dist/assets/images/greenBenefitsTruck.svg",
      rteTitle: '<div style="text-align: left;">Unlimited Free Delivery on orders over $30</div>\r\n',
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "benefitsTruck",
    },
    freshpass_tile_item__1519499692: {
      fpImage: "ocsp-web-ui/dist/assets/images/monthlycredit.svg",
      rteTitle: "$5 Monthly Credit for Annual Subscribers",
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "$5 monthly credit for annual subscribers",
    },
    freshpass_tile_item_: {
      fpImage: "ocsp-web-ui/dist/assets/images/save20.svg",
      rteTitle: "Save $20 Off Your First Online Order Over $75",
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "save 20 image",
    },
    freshpass_tile_item__797202485: {
      fpImage: "ocsp-web-ui/dist/assets/images/2xpoints.svg",
      rteTitle: '<div style="text-align: left;">Earn 2X Reward Points on Our Exclusive Brands</div>\r\n',
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "Earn 2X Reward Points on Our Exclusive Brands",
    },
    freshpass_tile_item__566169671: {
      fpImage: "ocsp-web-ui/dist/assets/images/organic.svg",
      rteTitle: "Save 5% on O Organics<sup>®</sup> and Open Nature<sup>®</sup>",
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "Save 5% on O Organics and Open Nature",
    },
    freshpass_tile_item__1650940289: {
      fpImage: "ocsp-web-ui/dist/assets/images/pet.svg",
      rteTitle: "Save 5% on Signature Pet Care<sup>®</sup>",
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "Save 5% on Signature Pet Care",
    },
    freshpass_tile_item__1951713976: {
      fpImage: "ocsp-web-ui/dist/assets/images/rewardsDontExpire.svg",
      rteTitle: "Rewards Don't Expire",
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "Rewards don't expire",
    },
    freshpass_tile_item__1555375445: {
      fpImage: "ocsp-web-ui/dist/assets/images/starbucksCupArt.svg",
      rteTitle: "Monthly Exclusive Starbucks<sup>®</sup> Perks",
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "starbucks cup",
    },
    freshpass_tile_item__1944760550: {
      fpImage: "ocsp-web-ui/dist/assets/images/vipPhoneLine.svg",
      rteTitle: "VIP Phone Line",
      rule: "static",
      infoIcon: "false",
      textIsRich: "true",
      tileImageAltText: "vip phone line",
    },
  },
  benefits_DUG5: {
    acme: {
      freshpass_tile_item__1: {
        fpImage: "ocsp-web-ui/dist/assets/images/5OffDUG.svg",
        rteTitle: "5% off Drive Up & Go™ orders** Ends 8/15",
        rule: "static",
        infoIcon: "false",
        textIsRich: "true",
        tileImageAltText: "drive up and go image",
      },
    },
    jewelosco: {
      freshpass_tile_item__2: {
        fpImage: "ocsp-web-ui/dist/assets/images/5OffDUG.svg",
        rteTitle: "5% off Drive Up & Go™ orders** Ends 6/15",
        rule: "static",
        infoIcon: "false",
        textIsRich: "true",
        tileImageAltText: "drive up and go image",
      },
    },
  },
  featureFlags,
  freshpassRestrictedZipcodes: ["86040"],
  programType: {
    forUPlus: forUPlusData,
    freshpass: freshPassData,
  }
};

